
import {
  GAME_ENDPOINTS,
} from '../../../api';

import api from '../../../api/axios';

import type {
  CreateGameRequest,
  CreateTemplateRequest,
  GameStatus,
  GameTemplate,
  PaymentRequest,
  PlayGame,
  Prize,
  UpdateBlindRequest,
  UpdateReEntryRequest,
  UpdateTemplateBasicRequest,
} from '../types/game.types';

import {
  getTodayDate,
  getYesterdayDate,
} from '../../../shared/utils/timeUtils';


/**
 * 게임 목록 조회 Query Parameter
 */
interface FetchGamesByStoreParams {
  gameDay?: string;
  status?: GameStatus[];
}


/* =========================================================
   진행 게임 조회
   ========================================================= */

/**
 * 지점별 게임 목록 조회
 */
export const fetchGamesByStore = async (
  storeId: number,
  gameDay?: string,
  status?: GameStatus[],
): Promise<PlayGame[]> => {
  const params: FetchGamesByStoreParams = {};

  if (gameDay) {
    params.gameDay = gameDay;
  }

  if (
    status &&
    status.length > 0
  ) {
    params.status = status;
  }

  const response =
    await api.get(
      GAME_ENDPOINTS.PLAY_GAME.BY_STORE(
        storeId,
      ),
      {
        params,
      },
    );

  return response.data.data;
};


/**
 * 오늘 게임 +
 * 어제부터 진행 중인 게임 조회
 */
export const fetchGamesForToday = async (
  storeId: number,
): Promise<PlayGame[]> => {
  const today =
    getTodayDate();

  const yesterday =
    getYesterdayDate();

  try {
    const [
      todayGames,
      yesterdayRunningGames,
    ] =
      await Promise.all([
        fetchGamesByStore(
          storeId,
          today,
        ),

        fetchGamesByStore(
          storeId,
          yesterday,
          [
            'STARTED',
            'RUNNING',
          ],
        ),
      ]);

    /**
     * playGameId 기준 중복 제거
     */
    const gameMap =
      new Map<
        number,
        PlayGame
      >();

    todayGames.forEach(
      (
        game,
      ) => {
        gameMap.set(
          game.playGameId,
          game,
        );
      },
    );

    yesterdayRunningGames.forEach(
      (
        game,
      ) => {
        if (
          !gameMap.has(
            game.playGameId,
          )
        ) {
          gameMap.set(
            game.playGameId,
            game,
          );
        }
      },
    );

    return Array.from(
      gameMap.values(),
    );
  } catch (error: unknown) {
    console.error(
      '게임 목록 조회 실패:',
      error,
    );

    /**
     * 어제 게임 조회 실패 시
     * 오늘 게임만 재조회
     */
    return fetchGamesByStore(
      storeId,
      today,
    );
  }
};


/**
 * 게임 상세 조회
 */
export const fetchGameDetails = async (
  playGameId: number,
): Promise<PlayGame> => {
  const response =
    await api.get(
      GAME_ENDPOINTS.PLAY_GAME.DETAIL(
        playGameId,
      ),
    );

  return response.data.data;
};


/* =========================================================
   진행 게임 관리
   ADMIN
   ========================================================= */

/**
 * 게임 생성
 */
export const createPlayGame = async (
  data: CreateGameRequest,
): Promise<PlayGame> => {
  const response =
    await api.post(
      GAME_ENDPOINTS
        .ADMIN_PLAY_GAME
        .CREATE,
      data,
    );

  return response.data.data;
};


/**
 * 게임 시작
 */
export const startGame = async (
  playGameId: number,
): Promise<PlayGame> => {
  const response =
    await api.patch(
      GAME_ENDPOINTS
        .ADMIN_PLAY_GAME
        .START(
          playGameId,
        ),
    );

  return response.data.data;
};


/**
 * 게임 일시정지
 */
export const pauseGame = async (
  playGameId: number,
): Promise<PlayGame> => {
  const response =
    await api.patch(
      GAME_ENDPOINTS
        .ADMIN_PLAY_GAME
        .PAUSE(
          playGameId,
        ),
    );

  return response.data.data;
};


/**
 * 게임 재개
 */
export const resumeGame = async (
  playGameId: number,
): Promise<PlayGame> => {
  const response =
    await api.patch(
      GAME_ENDPOINTS
        .ADMIN_PLAY_GAME
        .RESUME(
          playGameId,
        ),
    );

  return response.data.data;
};


/**
 * 게임 종료
 */
export const finishGame = async (
  playGameId: number,
): Promise<PlayGame> => {
  const response =
    await api.patch(
      GAME_ENDPOINTS
        .ADMIN_PLAY_GAME
        .FINISH(
          playGameId,
        ),
    );

  return response.data.data;
};


/* =========================================================
   플레이어
   ========================================================= */

/**
 * 회원 플레이어 참가
 */
export const joinPlayer = async (
  playGameId: number,
  userId: number,
): Promise<PlayGame> => {
  const response =
    await api.post(
      GAME_ENDPOINTS
        .ADMIN_PLAY_GAME
        .JOIN_USER(
          playGameId,
        ),
      {
        userId,
      },
    );

  return response.data.data;
};


/**
 * 비회원 참가
 */
export const joinGuest = async (
  playGameId: number,
  guestName: string,
): Promise<PlayGame> => {
  const response =
    await api.post(
      GAME_ENDPOINTS
        .ADMIN_PLAY_GAME
        .JOIN_GUEST(
          playGameId,
        ),
      {
        guestName,
      },
    );

  return response.data.data;
};


/**
 * WAIT 플레이어 승인
 */
export const approvePlayer = async (
  playGameId: number,
  userGameId: number,
): Promise<PlayGame> => {
  const response =
    await api.patch(
      GAME_ENDPOINTS
        .ADMIN_PLAY_GAME
        .APPROVE_PLAYER(
          playGameId,
          userGameId,
        ),
    );

  return response.data.data;
};


/**
 * 플레이어 참가 요청 삭제
 */
export const deletePlayerJoinRequest = async (
  playGameId: number,
  userGameId: number,
): Promise<void> => {
  await api.delete(
    GAME_ENDPOINTS
      .ADMIN_PLAY_GAME
      .PLAYER(
        playGameId,
        userGameId,
      ),
  );
};


/**
 * 플레이어 탈락
 */
export const removePlayer = async (
  playGameId: number,
  userGameId: number,
): Promise<PlayGame> => {
  const response =
    await api.patch(
      GAME_ENDPOINTS
        .ADMIN_PLAY_GAME
        .OUT_PLAYER(
          playGameId,
          userGameId,
        ),
    );

  return response.data.data;
};


/**
 * 플레이어 리바인
 */
export const rebuyPlayer = async (
  playGameId: number,
  userGameId: number,
): Promise<PlayGame> => {
  const response =
    await api.patch(
      GAME_ENDPOINTS
        .ADMIN_PLAY_GAME
        .REBUY(
          playGameId,
          userGameId,
        ),
    );

  return response.data.data;
};


/**
 * 리바인 취소
 *
 * 현재 Endpoint 구조에서는
 * userGameId를 URL로 전달
 */
export const cancelRebuy = async (
  playGameId: number,
  userGameId: number,
): Promise<PlayGame> => {
  const response =
    await api.patch(
      GAME_ENDPOINTS
        .ADMIN_PLAY_GAME
        .CANCEL_REBUY(
          playGameId,
          userGameId,
        ),
    );

  return response.data.data;
};


/* =========================================================
   결제
   ========================================================= */

/**
 * 플레이어 결제 처리
 */
export const updatePayment = async (
  userGameId: number,
  data: PaymentRequest,
): Promise<void> => {
  await api.patch(
    GAME_ENDPOINTS
      .ADMIN_PLAY_GAME
      .PAYMENT(
        userGameId,
      ),
    data,
  );
};


/**
 * 플레이어 결제 취소
 */
export const cancelPayment = async (
  userGameId: number,
): Promise<void> => {
  await api.patch(
    GAME_ENDPOINTS
      .ADMIN_PLAY_GAME
      .CANCEL_PAYMENT(
        userGameId,
      ),
  );
};


/* =========================================================
   얼리칩
   ========================================================= */

/**
 * 얼리칩 증가
 */
export const increaseEarlyChip = async (
  playGameId: number,
): Promise<PlayGame> => {
  const response =
    await api.patch(
      GAME_ENDPOINTS
        .ADMIN_PLAY_GAME
        .EARLY_INCREASE(
          playGameId,
        ),
    );

  return response.data.data;
};


/**
 * 얼리칩 감소
 */
export const decreaseEarlyChip = async (
  playGameId: number,
): Promise<PlayGame> => {
  const response =
    await api.patch(
      GAME_ENDPOINTS
        .ADMIN_PLAY_GAME
        .EARLY_DECREASE(
          playGameId,
        ),
    );

  return response.data.data;
};


/* =========================================================
   Level
   ========================================================= */

/**
 * 이전 레벨
 */
export const prevLevel = async (
  playGameId: number,
): Promise<PlayGame> => {
  const response =
    await api.patch(
      GAME_ENDPOINTS
        .ADMIN_PLAY_GAME
        .PREV_LEVEL(
          playGameId,
        ),
    );

  return response.data.data;
};


/**
 * 다음 레벨
 */
export const nextLevel = async (
  playGameId: number,
): Promise<PlayGame> => {
  const response =
    await api.patch(
      GAME_ENDPOINTS
        .ADMIN_PLAY_GAME
        .NEXT_LEVEL(
          playGameId,
        ),
    );

  return response.data.data;
};


/* =========================================================
   Prize
   ========================================================= */

/**
 * 프라이즈 설정
 */
export const setPrizes = async (
  playGameId: number,
  prizes: Prize[],
): Promise<void> => {
  await api.post(
    GAME_ENDPOINTS
      .ADMIN_PLAY_GAME
      .PRIZES(
        playGameId,
      ),
    {
      prizes,
    },
  );
};


/* =========================================================
   게임 템플릿
   ========================================================= */

/**
 * 게임 템플릿 목록 조회
 */
export const fetchGameTemplates =
  async (): Promise<
    GameTemplate[]
  > => {
    const response =
      await api.get(
        GAME_ENDPOINTS
          .ADMIN
          .LIST,
      );

    return response.data.data;
  };


/**
 * 게임 템플릿 생성
 */
export const createGameTemplate =
  async (
    data: CreateTemplateRequest,
  ): Promise<GameTemplate> => {
    const response =
      await api.post(
        GAME_ENDPOINTS
          .ADMIN
          .CREATE,
        data,
      );

    return response.data.data;
  };


/**
 * 게임 템플릿 삭제
 */
export const deleteGameTemplate =
  async (
    gameId: number,
  ): Promise<void> => {
    await api.delete(
      GAME_ENDPOINTS
        .ADMIN
        .DETAIL(
          gameId,
        ),
    );
  };


/**
 * 게임 템플릿 기본 정보 수정
 */
export const updateTemplateBasic =
  async (
    gameId: number,
    data: UpdateTemplateBasicRequest,
  ): Promise<GameTemplate> => {
    const response =
      await api.patch(
        GAME_ENDPOINTS
          .ADMIN
          .DETAIL(
            gameId,
          ),
        data,
      );

    return response.data.data;
  };


/* =========================================================
   Blind
   ========================================================= */

/**
 * 블라인드 개별 수정
 */
export const updateBlind = async (
  gameId: number,
  level: number,
  data: UpdateBlindRequest,
): Promise<void> => {
  await api.patch(
    GAME_ENDPOINTS
      .ADMIN
      .BLIND(
        gameId,
        level,
      ),
    data,
  );
};


/**
 * 블라인드 개별 삭제
 */
export const deleteBlind = async (
  gameId: number,
  level: number,
): Promise<void> => {
  await api.delete(
    GAME_ENDPOINTS
      .ADMIN
      .BLIND(
        gameId,
        level,
      ),
  );
};


/* =========================================================
   Re-entry
   ========================================================= */

/**
 * 리엔트리 개별 수정
 */
export const updateReEntry = async (
  gameId: number,
  count: number,
  data: UpdateReEntryRequest,
): Promise<void> => {
  await api.patch(
    GAME_ENDPOINTS
      .ADMIN
      .RE_ENTRY(
        gameId,
        count,
      ),
    data,
  );
};


/**
 * 리엔트리 개별 삭제
 */
export const deleteReEntry = async (
  gameId: number,
  count: number,
): Promise<void> => {
  await api.delete(
    GAME_ENDPOINTS
      .ADMIN
      .RE_ENTRY(
        gameId,
        count,
      ),
  );
};